import React from 'react';
import { Box, FormControl, FormControlLabel, RadioGroup, Typography } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { AppRoundedRadio } from '@vyce/core/src/components/inputs/AppRadio';
import { AppKeyboardTimePicker } from '@vyce/core/src/components/inputs/AppKeyboardTimePicker';
import { ControlledSelect, ControlledTagAutocomplete } from '@vyce/core/src/components/controlled-inputs';
import { TimeAndAttendanceLocation } from '@vyce/core/src/types';
import { StaffItem } from '../hooks/useStaffSearch';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DAYS_OPTIONS = [
  { value: 'every_day', name: 'Every Day' },
  { value: 'every_weekday', name: 'Every Weekday' },
  { value: 'select_days', name: 'Select days' },
];

const dayItems = WEEKDAYS.map((name, i) => ({ name, uuid: String(i) }));

interface Props {
  fieldPrefix: string;
  locations: TimeAndAttendanceLocation[];
  boldClassName?: string;
  staff?: StaffItem[];
  setStaffSubstring?: (substring: string) => void;
}

export const SnapshotFormFields: React.FC<Props> = ({
  fieldPrefix,
  locations,
  boldClassName = '',
  staff = [],
  setStaffSubstring,
}) => {
  const { control, getValues, setValue, watch } = useFormContext();

  const toggleLocation = (location: TimeAndAttendanceLocation) => {
    const key = location.uuid ?? location.name;
    const ids = getValues(`${fieldPrefix}.selectedLocationIds`) || [];
    const next = ids.includes(key) ? ids.filter((id: string) => id !== key) : [...ids, key];
    setValue(`${fieldPrefix}.selectedLocationIds`, next);
  };

  const toggleAllLocations = () => {
    const ids = getValues(`${fieldPrefix}.selectedLocationIds`) || [];
    const allIds = locations.map(l => l.uuid ?? l.name);
    const next = ids.length === locations.length && locations.length > 0 ? [] : allIds;
    setValue(`${fieldPrefix}.selectedLocationIds`, next);
  };

  const selectedIds = watch(`${fieldPrefix}.selectedLocationIds`) || [];
  const daysOption = watch(`${fieldPrefix}.daysOption`);

  return (
    <Box display="flex" flexDirection="column" gridGap={24} padding={1}>
      <Box>
        <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
          Which Locations should be included?
        </Typography>
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Box display="flex" alignItems="center" gridGap={8}>
            <AppCheckbox
              checked={selectedIds.length === locations.length && locations.length > 0}
              onChange={toggleAllLocations}
              variant="rectangle"
              color="primary"
            />
            <Typography variant="body2">All Locations</Typography>
          </Box>
          {(locations ?? []).map(location => {
            const key = location.uuid ?? location.name;
            return (
              <Box key={key} display="flex" alignItems="center" gridGap={8}>
                <AppCheckbox
                  checked={selectedIds.includes(key)}
                  onChange={() => toggleLocation(location)}
                  variant="rectangle"
                  color="primary"
                />
                <Typography variant="body2">{location.name}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
          What days should be included?
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="days"
            value={watch(`${fieldPrefix}.daysOption`)}
            onChange={e => setValue(`${fieldPrefix}.daysOption`, e.target.value)}
          >
            {DAYS_OPTIONS.map(opt => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<AppRoundedRadio color="primary" />}
                label={opt.name}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {daysOption === 'select_days' && (
        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            Which day should the email be sent?
          </Typography>
          <ControlledSelect
            name={`${fieldPrefix}.sendDay`}
            items={dayItems}
            label="Select day"
            multiple={false}
          />
        </Box>
      )}

      <Box>
        <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
          What time should the email be sent?
        </Typography>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Box maxWidth={200}>
            <Controller
              name={`${fieldPrefix}.sendTime`}
              control={control}
              render={({ field: timeField }) => (
                <AppKeyboardTimePicker
                  ampm={false}
                  mask="__:__"
                  views={['hours']}
                  value={timeField.value || null}
                  onChange={date => {
                    const d = date ? new Date(date) : null;
                    if (d) d.setMinutes(0, 0, 0);
                    timeField.onChange(d);
                  }}
                  label="Set the time"
                  fullWidth
                />
              )}
            />
          </Box>
        </MuiPickersUtilsProvider>
      </Box>

      <Box>
        <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
          Who to?
        </Typography>
        <ControlledTagAutocomplete
          name={`${fieldPrefix}.receivers`}
          items={staff}
          label="Select recipients"
          multiple
          setSubstring={setStaffSubstring}
          margin="none"
        />
      </Box>
    </Box>
  );
};
